import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const ROLE_LABELS = {
  'BROKER_ADMIN': { name: 'Broker Admin', color: '#dc2626', description: 'Full system access' },
  'BROKER_AGENT': { name: 'Broker Agent', color: '#3b82f6', description: 'Day-to-day operations' },
  'ACCOUNTING': { name: 'Accounting', color: '#10b981', description: 'Financial operations' },
  'SHIPPER': { name: 'Shipper', color: '#8b5cf6', description: 'Shipper portal' },
  'CARRIER': { name: 'Carrier', color: '#f59e0b', description: 'Carrier portal' }
};

const CATEGORY_LABELS = {
  'LOADS': '📦 Loads',
  'CRM': '👥 CRM',
  'TARIFFS': '🌎 Tariffs',
  'SAP': '⚙️ SAP Integration',
  'SUSTAINABILITY': '🌱 Sustainability',
  'FACTORING': '💵 Quick Pay',
  'INVOICING': '📄 Invoicing',
  'SETTLEMENTS': '💰 Settlements',
  'EXPORTS': '📊 Exports',
  'CARRIERS': '🚛 Carriers',
  'CUSTOMERS': '🏢 Customers',
  'TRACKING': '📍 Tracking',
  'WELLSITES': '🛢️ Wellsites',
  'RATES': '📈 Rate Insights',
  'ADMIN': '🔐 Admin'
};

export default function AdminRoles() {
  const { adminEmail } = useAuth();
  const [roles, setRoles] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState('BROKER_AGENT');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        fetch('/api/permissions/roles', { headers: { 'x-admin-email': adminEmail } }).then(r => r.json()),
        fetch('/api/permissions/all', { headers: { 'x-admin-email': adminEmail } }).then(r => r.json())
      ]);
      setRoles(rolesRes.roles || {});
      setAllPermissions(permsRes.grouped || {});
      
      // Set initial edited permissions for selected role
      const rolePerms = rolesRes.roles?.[selectedRole]?.permissions || [];
      setEditedPermissions(rolePerms.map(p => p.code));
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (roles[selectedRole]) {
      setEditedPermissions(roles[selectedRole].permissions.map(p => p.code));
    }
  }, [selectedRole, roles]);

  const togglePermission = (code) => {
    if (selectedRole === 'BROKER_ADMIN') return; // Can't edit admin
    setEditedPermissions(prev => 
      prev.includes(code) ? prev.filter(p => p !== code) : [...prev, code]
    );
  };

  const toggleCategory = (category, perms) => {
    if (selectedRole === 'BROKER_ADMIN') return;
    const codes = perms.map(p => p.code);
    const allSelected = codes.every(c => editedPermissions.includes(c));
    
    if (allSelected) {
      setEditedPermissions(prev => prev.filter(p => !codes.includes(p)));
    } else {
      setEditedPermissions(prev => [...new Set([...prev, ...codes])]);
    }
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/permissions/roles/${selectedRole}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' },
        body: JSON.stringify({ permissions: editedPermissions })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      alert('Permissions saved!');
      loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    const original = roles[selectedRole]?.permissions?.map(p => p.code) || [];
    return JSON.stringify([...original].sort()) !== JSON.stringify([...editedPermissions].sort());
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>🔐 Roles & Permissions</h1>
      <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>Manage access control for each user role</p>

      {/* Role Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {Object.entries(ROLE_LABELS).map(([role, info]) => (
          <button
            key={role}
            onClick={() => setSelectedRole(role)}
            style={{
              padding: '12px 20px',
              borderRadius: 8,
              border: selectedRole === role ? `2px solid ${info.color}` : '2px solid #e5e7eb',
              background: selectedRole === role ? `${info.color}10` : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontWeight: 700, color: info.color }}>{info.name}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{info.description}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              {roles[role]?.permissions?.length || 0} permissions
            </div>
          </button>
        ))}
      </div>

      {/* Permission Editor */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 18, color: ROLE_LABELS[selectedRole]?.color }}>
              {ROLE_LABELS[selectedRole]?.name}
            </span>
            <span style={{ marginLeft: 12, fontSize: 13, color: '#6b7280' }}>
              {editedPermissions.length} permissions selected
            </span>
          </div>
          {selectedRole !== 'BROKER_ADMIN' && (
            <button
              onClick={savePermissions}
              disabled={saving || !hasChanges()}
              style={{
                padding: '10px 20px',
                background: saving || !hasChanges() ? '#e5e7eb' : '#dc2626',
                color: saving || !hasChanges() ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: saving || !hasChanges() ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : hasChanges() ? 'Save Changes' : 'No Changes'}
            </button>
          )}
        </div>

        {selectedRole === 'BROKER_ADMIN' && (
          <div style={{ padding: 16, background: '#fef2f2', color: '#dc2626', fontSize: 13 }}>
            ⚠️ Broker Admin has full access to all permissions and cannot be modified.
          </div>
        )}

        <div style={{ padding: 16 }}>
          {Object.entries(allPermissions).map(([category, perms]) => {
            const categoryPerms = perms || [];
            const selectedCount = categoryPerms.filter(p => editedPermissions.includes(p.code)).length;
            const allSelected = selectedCount === categoryPerms.length;
            
            return (
              <div key={category} style={{ marginBottom: 24 }}>
                <div 
                  onClick={() => toggleCategory(category, categoryPerms)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: 12, 
                    cursor: selectedRole !== 'BROKER_ADMIN' ? 'pointer' : 'default' 
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => {}}
                    disabled={selectedRole === 'BROKER_ADMIN'}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>
                    {CATEGORY_LABELS[category] || category}
                  </span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    ({selectedCount}/{categoryPerms.length})
                  </span>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8, marginLeft: 26 }}>
                  {categoryPerms.map(perm => (
                    <label
                      key={perm.code}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        background: editedPermissions.includes(perm.code) ? '#f0fdf4' : '#f9fafb',
                        borderRadius: 6,
                        cursor: selectedRole !== 'BROKER_ADMIN' ? 'pointer' : 'default',
                        border: editedPermissions.includes(perm.code) ? '1px solid #bbf7d0' : '1px solid #e5e7eb'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editedPermissions.includes(perm.code)}
                        onChange={() => togglePermission(perm.code)}
                        disabled={selectedRole === 'BROKER_ADMIN'}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{perm.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{perm.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Role Matrix Quick View */}
      <div style={{ marginTop: 24, background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: '0 0 16px 0' }}>📊 Permission Matrix Overview</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 10, textAlign: 'left', fontWeight: 700 }}>Category</th>
                {Object.keys(ROLE_LABELS).map(role => (
                  <th key={role} style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: ROLE_LABELS[role].color }}>
                    {ROLE_LABELS[role].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(CATEGORY_LABELS).map(([cat, label]) => (
                <tr key={cat} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: 10, fontWeight: 600 }}>{label}</td>
                  {Object.keys(ROLE_LABELS).map(role => {
                    const rolePerms = roles[role]?.categories?.[cat] || [];
                    const totalPerms = allPermissions[cat]?.length || 0;
                    const pct = totalPerms > 0 ? Math.round((rolePerms.length / totalPerms) * 100) : 0;
                    
                    return (
                      <td key={role} style={{ padding: 10, textAlign: 'center' }}>
                        {role === 'BROKER_ADMIN' ? (
                          <span style={{ color: '#10b981' }}>✓ Full</span>
                        ) : rolePerms.length > 0 ? (
                          <span style={{ 
                            padding: '2px 8px', 
                            background: pct === 100 ? '#d1fae5' : '#fef3c7',
                            color: pct === 100 ? '#065f46' : '#92400e',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {rolePerms.length}/{totalPerms}
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
